import { ArrowLeft, Search } from 'lucide-react';
import type { ReactNode } from 'react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../Button';
import FullscreenToggle from '../FullscreenToggle';
import Input from '../Input';
import ProgressIndicator from '../ProgressIndicator';
import Select, { type SelectOption } from '../Select';
import QuestionSelector from '@/pages/ModuleLauncher/shared/QuestionSelector';

export interface PageHeaderProps {
    title: string;

    searchProps?: {
        value: string;
        onChange: (value: string) => void;
        placeholder?: string;
        ariaLabel?: string;
    };
    selectionProps?: {
        name: string;
        value: string | number | null;
        onChange: (value: string | number | null) => void;
        options: SelectOption[];
        placeholder?: string;
        loading?: boolean;
        error?: string;
    };

    leftSelectionProps?: {
        name: string;
        value: string | number | null;
        onChange: (value: string | number | null) => void;
        options: SelectOption[];
        placeholder?: string;
        loading?: boolean;
        error?: string;
    };
    actionButton?: {
        label: string;
        onClick: () => void;
        show?: boolean;
        icon?: ReactNode;
        disabled?: boolean;
    };
    subActionButton?: {
        label: string;
        show?: boolean;
        onClick: () => void;
    };
    className?: string;
    onBack?: () => void;
    actionBack?: {
        showBackButton?: boolean;
        onBack?: () => void;
    };
    fullscreenToggle?: {
        show?: boolean;
        targetRef?: React.RefObject<HTMLElement | null>;
        size?: 'sm' | 'md' | 'lg';
        variant?: 'default' | 'minimal';
        className?: string;
        isFullscreen?: boolean;
        onToggle?: () => void;
        isMobile?: boolean;
    };
    extraContent?: ReactNode;
    extraLeftContent?: ReactNode;
    headerMobileSameline?: boolean;
    extraContentMobileRight?: boolean;
    noControlsRowGap?: boolean;
    progressBarPercentage?: number;
}

const PageHeader = ({
    title,
    searchProps,
    selectionProps,
    leftSelectionProps,
    actionButton,
    className = '',
    actionBack,
    subActionButton,
    fullscreenToggle,
    extraContent,
    extraLeftContent,
    headerMobileSameline,
    extraContentMobileRight,
    noControlsRowGap = false,
    progressBarPercentage,
}: PageHeaderProps) => {
    const { t } = useTranslation('common');
    const searchInputRef = useRef<HTMLInputElement>(null);

    return (
        <div className={`flex flex-col gap-5 lg:gap-10 ${className}`}>
            {/* Title and Controls Row */}
            <div
                className={`flex ${noControlsRowGap ? '' : 'gap-5'} sm:items-center sm:justify-between ${headerMobileSameline ? 'flex-row items-center' : 'flex-col sm:flex-row'}`}
            >
                <div className="flex items-start">
                    <div className="flex flex-col items-start gap-1">
                        <div className="flex items-center">
                            {actionBack && actionBack.showBackButton && (
                                <button
                                    onClick={actionBack.onBack}
                                    className="mr-4 flex items-center text-gray-500 transition-colors hover:text-gray-700"
                                    aria-label="back"
                                >
                                    <ArrowLeft className="size-5" strokeWidth={1.5} />
                                </button>
                            )}
                            <h1 className="text-xl font-bold text-neutral-900 lg:text-2xl">{title}</h1>
                        </div>
                        {(leftSelectionProps || extraLeftContent) && (
                            <div
                                className={
                                    actionBack?.showBackButton
                                        ? 'ml-9 flex flex-col items-start gap-1'
                                        : 'flex flex-col items-start gap-1'
                                }
                            >
                                {leftSelectionProps && (
                                    <QuestionSelector
                                        value={leftSelectionProps.value}
                                        onChange={leftSelectionProps.onChange}
                                        options={leftSelectionProps.options}
                                        placeholder={leftSelectionProps.placeholder}
                                        dropdownWidth={250}
                                    />
                                )}
                                {extraLeftContent && (
                                    <div className="justify-start text-center text-base leading-6 font-semibold text-neutral-500">
                                        {extraLeftContent}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Controls: Company Selection and Action Button */}
                <div
                    className={`flex shrink-0 flex-col gap-4 sm:flex-row sm:items-center ${extraContentMobileRight ? 'flex-1 sm:flex-none' : ''}`}
                >
                    {selectionProps && (
                        <Select
                            name={selectionProps.name}
                            value={selectionProps.value}
                            onChange={selectionProps.onChange}
                            options={selectionProps.options}
                            placeholder={selectionProps.placeholder}
                            className="w-full sm:w-64"
                        />
                    )}
                    <div
                        className={`grid items-center gap-x-3 gap-y-5 sm:flex ${extraContentMobileRight ? 'justify-end sm:justify-start' : ''}`}
                    >
                        {subActionButton?.show !== false && subActionButton && (
                            <div>
                                <Button onClick={subActionButton.onClick} variant="secondary" className="sm:h-[38px]">
                                    {subActionButton.label}
                                </Button>
                            </div>
                        )}
                        {actionButton?.show !== false && actionButton && (
                            <div>
                                <Button
                                    type="button"
                                    variant="primary"
                                    onClick={actionButton.onClick}
                                    className="flex items-center gap-1.5 px-2.5 whitespace-nowrap sm:h-[38px]"
                                    disabled={actionButton.disabled}
                                >
                                    {actionButton.icon}
                                    {actionButton.label}
                                </Button>
                            </div>
                        )}
                        {/* Custom extra content positioned before fullscreen toggle */}
                        {extraContent && <div className="flex items-center">{extraContent}</div>}
                        {progressBarPercentage && <ProgressIndicator percentage={progressBarPercentage} />}
                        {/* Fullscreen toggle positioned at the very end */}
                        {fullscreenToggle?.show && !fullscreenToggle.isMobile && (
                            <FullscreenToggle
                                targetRef={fullscreenToggle.targetRef}
                                size={fullscreenToggle.size || 'sm'}
                                variant={fullscreenToggle.variant || 'minimal'}
                                className={fullscreenToggle.className || 'text-gray-500 hover:text-gray-700'}
                                isFullscreen={fullscreenToggle.isFullscreen}
                                onToggle={fullscreenToggle.onToggle}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Search */}
            {searchProps && (
                // Width matches design mockup specification
                <div className="flex w-full max-w-[473.50px] flex-col gap-1.5">
                    <label className="text-base leading-normal font-semibold text-neutral-800">
                        {t('navigation.search')}
                    </label>

                    <div className="relative">
                        <Input
                            ref={searchInputRef}
                            name="search"
                            placeholder={searchProps.placeholder}
                            value={searchProps.value}
                            onChange={e => searchProps.onChange(e.target.value)}
                            className="h-9 w-full border-neutral-300 pr-9 text-base font-medium placeholder:text-neutral-500 focus:border-neutral-300 focus:ring-neutral-300"
                            aria-label={searchProps.ariaLabel}
                        />
                        <Search
                            className="pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2 text-neutral-500"
                            strokeWidth={2}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default PageHeader;
